import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Globe, ScanSearch } from "lucide-react";
import type { LensRequest, LensResponse, PhotoMatchListing } from "../../shared/contracts.js";
import { api, errorMessage, photoUrl } from "../api.js";
import { fmtCLP, fmtInt, fmtPlate, fmtRelative, titleCase } from "../lib/format.js";
import { TIER_HINTS, TIER_LABELS, nearPlates } from "../lib/photos.js";
import { SourceBadge } from "./ListingBits.js";
import { Badge } from "./ui/Badge.js";
import { Button } from "./ui/Button.js";
import { ExternalLink } from "./ui/Misc.js";
import { Callout } from "./ui/States.js";

function SmallThumb({ src, alt }: { src: string; alt: string }) {
  return (
    <img src={src} alt={alt} loading="lazy" decoding="async"
      className="h-16 w-20 shrink-0 rounded-md border border-border bg-surface-2 object-cover sm:h-20 sm:w-28" />
  );
}

/**
 * Otros avisos con la misma foto: la foto de este aviso (o la subida) al lado de la del
 * otro, el tipo de coincidencia y las patentes del otro aviso. Una patente distinta no
 * se resalta (ver lib/photos.ts); una casi igual sí, como posible error de lectura.
 */
export function PhotoMatchList({ matches, basePlates, preview, max = 5 }: {
  matches: PhotoMatchListing[];
  basePlates: string[];
  /** Foto subida por el analista (object URL), cuando no es un aviso. */
  preview?: string;
  max?: number;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? matches : matches.slice(0, max);
  return (
    <div>
      <ul className="divide-y divide-border rounded-md border border-border">
        {shown.map((m) => {
          const pair = m.pairs[0]!;
          const vehicle = [titleCase(m.make), titleCase(m.model), m.year ? String(m.year) : ""].filter(Boolean).join(" ");
          const near = nearPlates(basePlates, m.plates);
          return (
            <li key={m.listingId} className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center">
              <div className="flex items-center gap-1.5">
                {pair.own
                  ? <SmallThumb src={photoUrl(pair.own.snapshotId, pair.own.index, "thumb")} alt="Foto de este aviso" />
                  : preview ? <SmallThumb src={preview} alt="Foto subida" /> : null}
                <ArrowRight className="size-4 shrink-0 text-subtle" aria-hidden="true" />
                <SmallThumb src={photoUrl(pair.other.snapshotId, pair.other.index, "thumb")} alt="Foto del otro aviso" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge tone={m.best === "misma" ? "accent" : "neutral"}
                    title={`${TIER_HINTS[m.best]} (diferencia de huella: ${pair.distance} de 64 bits)`}>
                    {TIER_LABELS[m.best]}
                  </Badge>
                  {m.pairs.length > 1 && <span className="text-[12px] text-subtle">{fmtInt(m.pairs.length)} fotos en común</span>}
                </div>
                <Link to={`/avisos/${encodeURIComponent(m.listingId)}`} className="block truncate font-medium text-accent hover:underline">
                  {m.title || "Aviso sin título"}
                </Link>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-subtle">
                  <SourceBadge source={m.source} />
                  {vehicle && <span>{vehicle}</span>}
                  {m.price !== null && <span className="tabular-nums">{fmtCLP(m.price)}</span>}
                  {m.seenAt && <span>· {fmtRelative(m.seenAt)}</span>}
                  {m.plates.length > 0 && (
                    <span className="font-mono">· {m.plates.map(fmtPlate).join(", ")}</span>
                  )}
                </div>
                {near.length > 0 && (
                  <Badge tone="warning" title="Difieren en uno o dos caracteres: puede ser la misma patente leída o escrita distinto. Revise las fotos.">
                    Patente casi igual: {near.map(([a, b]) => `${fmtPlate(a)} / ${fmtPlate(b)}`).join(", ")}
                  </Badge>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {matches.length > max && (
        <button type="button" onClick={() => setAll((s) => !s)} className="mt-2 text-[12.5px] font-medium text-accent hover:underline">
          {all ? "Mostrar menos" : `Ver los ${fmtInt(matches.length)} avisos`}
        </button>
      )}
    </div>
  );
}

export const photoStatusQuery = {
  queryKey: ["photos", "status"] as const,
  queryFn: ({ signal }: { signal?: AbortSignal }) => api.photos.status(signal),
  staleTime: 60_000,
};

/**
 * "Buscar esta foto en internet" (Google Lens). Solo muestra la misma foto o una muy
 * parecida; los ~60 autos parecidos que trae Google se descartan.
 */
export function LensSearch({ request, label = "Buscar esta foto en internet" }: { request: LensRequest; label?: string }) {
  const status = useQuery(photoStatusQuery);
  const lens = useMutation({ mutationFn: () => api.photos.lens(request) });
  if (!status.data) return null;
  if (!status.data.lens) {
    return <p className="text-[12.5px] text-subtle">{status.data.reason ?? "La búsqueda de fotos en internet no está disponible."}</p>;
  }
  const remaining = Math.max(0, status.data.dailyCap - (lens.data?.usedToday ?? status.data.usedToday));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" icon={<ScanSearch className="size-4" aria-hidden="true" />} loading={lens.isPending}
          disabled={remaining === 0 || lens.isSuccess} onClick={() => lens.mutate()}>
          {label}
        </Button>
        <span className="text-[12px] text-subtle">
          Envía la foto a Google Lens (queda registrado). Quedan {fmtInt(remaining)} de {fmtInt(status.data.dailyCap)} hoy.
        </span>
      </div>
      {lens.isError && <Callout tone="danger">{errorMessage(lens.error)}</Callout>}
      {lens.data && <LensResults r={lens.data} />}
    </div>
  );
}

function LensResults({ r }: { r: LensResponse }) {
  if (r.matches.length === 0) {
    return (
      <p className="text-[13px] text-muted">
        Google no encontró esta foto en otros sitios. Revisó {fmtInt(r.total)} resultados: eran fotos parecidas, no la misma.
      </p>
    );
  }
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] text-muted">
        Aparece en {fmtInt(r.matches.length)} {r.matches.length === 1 ? "página" : "páginas"} (de {fmtInt(r.total)} resultados de Google).
      </p>
      <ul className="divide-y divide-border rounded-md border border-border">
        {r.matches.map((m) => (
          <li key={m.link} className="flex items-center gap-3 px-3 py-2">
            {m.thumbnail
              ? <img src={m.thumbnail} alt="" className="h-14 w-20 shrink-0 rounded border border-border object-cover" />
              : <Globe className="size-5 shrink-0 text-subtle" aria-hidden="true" />}
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone={m.tier === "misma" ? "accent" : "neutral"} title={TIER_HINTS[m.tier]}>{TIER_LABELS[m.tier]}</Badge>
                <span className="text-[12px] text-subtle">{m.site || m.domain}</span>
              </div>
              <ExternalLink href={m.link} className="block truncate text-[13px]">{m.title || m.domain}</ExternalLink>
              {m.listingId && (
                <Link to={`/avisos/${encodeURIComponent(m.listingId)}`} className="text-[12px] font-medium text-accent hover:underline">
                  Ya está en Sentinel: ver aviso
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
